import React, { useState } from "react";
import ParagraphTag from "../../../reusebale/Paragraph";

const DonutChart = ({ percent }) => {
  const r = 40;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="14" />
      <circle
        cx="50" cy="50" r={r} fill="none"
        stroke="#01CB78" strokeWidth="14"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
};

const OverviewSection = ({ allDetails }) => {
  const [rateMode, setRateMode] = useState("annual");

  const totalUnits = allDetails?.unitCount ?? 0;
  const occupiedUnits = allDetails?.leaseUnitCount ?? 0;
  const vacantUnits = Math.max(totalUnits - occupiedUnits, 0);
  const occupiedPct = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  const unitData = allDetails?.unitData ?? [];
  const totalSqft = unitData.reduce((sum, u) => sum + (Number(u.size) || 0), 0);
  const occupiedSqft = unitData
    .filter((u) => u.occupied)
    .reduce((sum, u) => sum + (Number(u.tenantSize) || Number(u.size) || 0), 0);
  const vacantSqft = Math.max(totalSqft - occupiedSqft, 0);
  const sqftPct = totalSqft > 0 ? Math.round((occupiedSqft / totalSqft) * 100) : 0;

  const occupiedWithRent = unitData.filter(
    (u) => u.occupied && Number(u.rent) > 0 && Number(u.size) > 0
  );
  const avgPsfMonth =
    occupiedWithRent.length > 0
      ? occupiedWithRent.reduce((sum, u) => sum + Number(u.rent) / Number(u.size), 0) /
        occupiedWithRent.length
      : null;
  const avgPsfYear = avgPsfMonth != null ? avgPsfMonth * 12 : null;
  const totalAnnualRent = unitData
    .filter((u) => u.occupied)
    .reduce((sum, u) => sum + (Number(u.rent) || 0) * 12, 0);

  const fmt = (n) =>
    n?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-5 flex gap-4">
      {/* LEFT — Occupancy + Sqft */}
      <div className="w-[22%] flex flex-col gap-4">
        {/* Occupancy Card */}
        <div className="bg-white rounded-xl p-4">
          <ParagraphTag
            content="Property Occupancy"
            classes="text-darkColor font-popinsSemiBold text-sm mb-3"
          />
          <div className="relative w-[120px] h-[120px] mx-auto">
            <DonutChart percent={occupiedPct} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-darkColor font-popinsSemiBold text-xl">{occupiedPct}%</span>
              <span className="text-darkColor/50 font-popinsRegular text-[10px]">Occupied</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between text-center">
            <div>
              <p className="text-darkColor font-popinsSemiBold text-sm">{totalUnits}</p>
              <p className="text-darkColor/40 font-popinsRegular text-[10px]">Total Units</p>
            </div>
            <div>
              <p className="text-darkColor font-popinsSemiBold text-sm">{occupiedUnits}</p>
              <p className="text-darkColor/40 font-popinsRegular text-[10px]">Occupied</p>
            </div>
            <div>
              <p className="text-darkColor font-popinsSemiBold text-sm">{vacantUnits}</p>
              <p className="text-darkColor/40 font-popinsRegular text-[10px]">Vacant</p>
            </div>
          </div>
        </div>

        {/* Total Sqft Card */}
        <div className="bg-white rounded-xl p-4">
          <ParagraphTag
            content="Total Sqft"
            classes="text-darkColor font-popinsSemiBold text-sm mb-1"
          />
          <p className="text-darkColor/40 font-popinsRegular text-[10px]">Total Leasable Area</p>
          <p className="text-darkColor font-popinsSemiBold text-2xl mt-1">
            {totalSqft > 0 ? totalSqft.toLocaleString() : "0"}
            <span className="text-sm font-popinsRegular ml-1">Sqft</span>
          </p>
          <div className="mt-2 w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${sqftPct}%` }}
            />
          </div>
          <div className="mt-3 flex justify-between">
            <div>
              <p className="text-darkColor/40 font-popinsRegular text-[10px]">Occupied Sqft</p>
              <p className="text-darkColor font-popinsSemiBold text-xs">
                {occupiedSqft.toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-darkColor/40 font-popinsRegular text-[10px]">Vacant Sqft</p>
              <p className="text-darkColor font-popinsSemiBold text-xs">
                {vacantSqft.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CENTER — Property Image */}
      <div className="w-[44%]">
        <div className="bg-white rounded-xl overflow-hidden h-[400px]">
          <div className="relative w-full h-full">
            {allDetails?.thumbnailImage?.url ? (
              <img
                src={allDetails.thumbnailImage.url}
                alt={allDetails.propertyName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <p className="text-darkColor/30 font-popinsRegular text-sm">No image uploaded</p>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-4">
              <p className="text-white font-popinsSemiBold text-base">
                {allDetails?.propertyName || "Overall Property Overview"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — Info + Rate + Site Plan + Leases */}
      <div className="w-[34%] flex flex-col gap-4">
        {/* Property Information */}
        <div className="bg-white rounded-xl p-4">
          <ParagraphTag
            content="Property Information"
            classes="text-darkColor font-popinsSemiBold text-sm"
          />
          <p className="text-darkColor/50 font-popinsRegular text-xs mt-1 whitespace-pre-line leading-relaxed">
            {allDetails?.completeAddress || "N/A"}
          </p>
        </div>

        {/* Average Rental Rate */}
        <div className="bg-white rounded-xl p-4">
          <div className="flex justify-between items-center">
            <ParagraphTag
              content="Average Rental Rate"
              classes="text-darkColor font-popinsSemiBold text-sm"
            />
            <select
              value={rateMode}
              onChange={(e) => setRateMode(e.target.value)}
              style={{ border: "1px solid #E5E7EB" }}
              className="text-xs rounded-md pl-3 pr-6 py-1.5 w-28 font-popinsRegular text-darkColor focus:outline-none bg-white cursor-pointer"
            >
              <option value="annual">Show Annual</option>
              <option value="psf">Show PSF</option>
            </select>
          </div>
          {rateMode === "psf" ? (
            <div className="mt-2">
              <p className="text-darkColor font-popinsSemiBold text-2xl">
                {avgPsfYear != null ? `$${fmt(avgPsfYear)}` : "N/A"}
                <span className="text-sm font-popinsRegular text-darkColor/50 ml-1">PSF/YR</span>
              </p>
              {avgPsfMonth != null && (
                <p className="text-darkColor/40 font-popinsRegular text-xs mt-0.5">
                  ${fmt(avgPsfMonth)} PSF/Month
                </p>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-darkColor font-popinsSemiBold text-2xl">
                {totalAnnualRent > 0 ? `$${totalAnnualRent.toLocaleString()}` : "N/A"}
                <span className="text-sm font-popinsRegular text-darkColor/50 ml-1">Annual</span>
              </p>
            </div>
          )}
        </div>

        {/* Site Plan */}
        {allDetails?.sitePlan?.url && (
          <div className="bg-white rounded-xl p-4">
            <ParagraphTag
              content="Site Plan"
              classes="text-darkColor font-popinsSemiBold text-sm mb-2"
            />
            <img
              src={allDetails.sitePlan.url}
              alt="Site Plan"
              className="w-full h-[140px] object-cover rounded-lg"
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default OverviewSection;
